import { useState, type MouseEvent, type JSX } from 'react'

import {
  Button,
  Calendar,
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@ttab/elephant-ui'
import { cn } from '@ttab/elephant-ui/utils'
import { useQuery, useRegistry } from '@/hooks'
import { cva } from 'class-variance-authority'
import { format, isSameDay } from 'date-fns'
import { type ViewProps } from '../types'
import { newLocalDate } from '@/shared/datetime'
import { useTranslation } from 'react-i18next'

export const DatePicker = ({ date, changeDate, setDate, resetToday, forceYear = false, disabled = false }: {
  date: Date
  changeDate?: (event: MouseEvent<Element> | KeyboardEvent | undefined, props: ViewProps, target?: 'self') => void
  setDate?: (arg: string) => void
  resetToday?: () => void
  forceYear?: boolean
  disabled?: boolean
  asDialog?: boolean
}): JSX.Element => {
  const { locale, timeZone } = useRegistry()
  const [open, setOpen] = useState<boolean>(false)

  const [query, setQuery] = useQuery()
  const { t } = useTranslation('shared')
  const today = newLocalDate(timeZone)
  const selectedIsToday = isSameDay(date, today)

  const formattedDate = new Intl.DateTimeFormat(locale.code.full, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone
  }).format(date)

  const longFormattedDate = new Intl.DateTimeFormat(locale.code.full, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone
  }).format(date)

  const defaultDate = cva('', {
    variants: {
      forceYear: {
        true: 'hidden',
        false: '@3xl/view:hidden'
      }
    }
  })

  const longDate = cva('', {
    variants: {
      forceYear: {
        true: '',
        false: 'hidden @3xl/view:inline'
      }
    }
  })

  const buttonWidth = cva('', {
    variants: {
      forceYear: {
        true: 'w-[9.5rem]',
        false: 'w-[6.5rem] @3xl/view:w-[9.5rem]'
      }
    }
  })

  return (
    <Popover modal={true} open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        render={(
          <Button
            variant='outline'
            size='xs'
            className={cn('justify-center text-center font-normal text-sm whitespace-nowrap px-2', buttonWidth({ forceYear }))}
            onKeyDown={(event) => {
              if (event.key !== 'Escape') {
                event?.stopPropagation()
              }
            }}
          >
            <span className={cn(defaultDate({ forceYear }))}>{formattedDate}</span>
            <span className={cn(longDate({ forceYear }))}>{longFormattedDate}</span>
          </Button>
        )}
      />
      <PopoverContent
        className='w-auto p-0'
        allowPropagationForKeys={['Escape', 'ArrowDown', 'ArrowUp']}
      >
        <Calendar
          disabled={disabled}
          footer={selectedIsToday
            ? undefined
            : (
                <Button
                  variant='outline'
                  onClick={() => {
                    if (resetToday) {
                      resetToday?.()
                      return
                    }

                    setQuery({ ...query, from: undefined })
                  }}
                >
                  {t('toolbar.navigateToday')}
                </Button>
              )}
          mode='single'
          locale={locale.module}
          selected={date}
          defaultMonth={date}
          onSelect={(selectedDate) => {
            if (selectedDate) {
              if (changeDate) {
                changeDate(undefined, { ...query, from: format(selectedDate, 'yyyy-MM-dd') }, 'self')
              }

              if (setDate) {
                setDate(format(selectedDate, 'yyyy-MM-dd'))
              }
            }
            setOpen(false)
          }}
        />
      </PopoverContent>
    </Popover>

  )
}
